import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Mail, Lock, User as UserIcon, ArrowRight } from "lucide-react";

const GoogleIcon = () => (
  <svg viewBox="0 0 48 48" width="18" height="18" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

const AuthPage = ({ mode = "login" }) => {
  const [isLogin, setIsLogin] = useState(mode === "login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register, loginWithGoogle } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const redirect = loc.state?.from || "/dashboard";
  // When Protected bounced the user here mid-flow (session expired), tell them
  // exactly why and where they'll land after sign-in — otherwise it feels like
  // the payment button "just did nothing".
  const bouncedFrom = loc.state?.from;
  const isPaymentBounce = typeof bouncedFrom === "string" && /\/payment(\/|$)/.test(bouncedFrom);

  // Capture referral code from ?ref= so it survives page nav & Google OAuth redirect.
  // Also pre-fill the email from ?email= when a sponsor invite link brings the user here.
  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const ref = params.get("ref");
    if (ref && ref.trim()) localStorage.setItem("gp_ref", ref.trim().toUpperCase());
    const invitedEmail = params.get("email");
    if (invitedEmail && invitedEmail.trim()) setEmail(invitedEmail.trim().toLowerCase());
    // Force register-tab when an invite is present so the user isn't shown the sign-in form by accident
    if (params.get("invite") || invitedEmail) setIsLogin(false);
  }, [loc.search]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    // Cloudflare 520/521/522/524 = origin/edge hiccup. Retry once transparently
    // so the user doesn't see a scary red box for a transient blip.
    const runOnce = async () => {
      if (isLogin) await login(email, password);
      else await register(name, email, password);
    };
    const isTransient5xx = (err) => {
      const s = err?.response?.status;
      return s === 502 || s === 503 || s === 504 || s === 520 || s === 521 || s === 522 || s === 524
        || err?.code === "ERR_NETWORK";
    };
    try {
      try {
        await runOnce();
      } catch (firstErr) {
        if (!isTransient5xx(firstErr)) throw firstErr;
        // Wait ~800ms then retry once.
        await new Promise(r => setTimeout(r, 800));
        await runOnce();
      }
      nav(redirect);
    } catch (e2) {
      // Prefer a clean JSON detail. Fallback to friendly generic copy for HTML/network errors.
      const status = e2?.response?.status;
      const rawDetail = e2?.response?.data?.detail;
      let msg;
      if (typeof rawDetail === "string" && rawDetail.length < 200) {
        msg = rawDetail;
      } else if (status === 401) {
        msg = "Invalid email or password.";
      } else if (isTransient5xx(e2)) {
        msg = "Our servers are momentarily unreachable. Please try again in a few seconds.";
      } else {
        msg = "Something went wrong. Please try again.";
      }
      setErr(msg);
    } finally { setBusy(false); }
  };

  return (
    <section className="min-h-[85vh] bg-cream-100 py-16 flex items-center">
      <div className="max-w-md w-full mx-auto px-5">
        <div className="text-center mb-8">
          <p className="tracking-widest text-coral-300 text-xs font-semibold uppercase">{isLogin ? "Welcome back" : "Get Started"}</p>
          <h1 className="font-serif text-navy-700 text-4xl md:text-5xl mt-3">{isLogin ? <>Sign in to <span className="italic">your gallery</span></> : <>Create your <span className="italic">account</span></>}</h1>
          {bouncedFrom && isLogin && (
            <div data-testid="auth-bounce-banner" className="mt-5 mx-auto max-w-sm bg-amber-50 border border-amber-200 rounded-2xl p-3 text-sm text-amber-800">
              {isPaymentBounce
                ? "Please sign back in to complete your payment — we'll take you straight to checkout."
                : "Your session expired. Sign back in and we'll drop you exactly where you left off."}
            </div>
          )}
        </div>
        <div className="bg-white rounded-3xl soft-shadow p-8">
          <button
            type="button"
            onClick={loginWithGoogle}
            data-testid="google-signin-btn"
            className="w-full flex items-center justify-center gap-3 border border-cream-300 rounded-full py-3 text-navy-700 font-semibold hover:bg-cream-100 transition-colors"
          >
            <GoogleIcon />
            <span>Continue with Google</span>
          </button>
          <div className="my-5 flex items-center gap-3 text-xs text-navy-700/50 uppercase tracking-wider">
            <span className="h-px bg-cream-200 flex-1" />
            or with email
            <span className="h-px bg-cream-200 flex-1" />
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {!isLogin && (
              <div>
                <label className="text-xs font-semibold text-navy-700 uppercase tracking-wider">Your name</label>
                <div className="relative mt-2">
                  <UserIcon className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-navy-700/40" />
                  <input required value={name} onChange={(e) => setName(e.target.value)} data-testid="auth-name-input" className="w-full pl-10 pr-4 py-3 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300" placeholder="Jane Doe" />
                </div>
              </div>
            )}
            <div>
              <label className="text-xs font-semibold text-navy-700 uppercase tracking-wider">Email</label>
              <div className="relative mt-2">
                <Mail className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-navy-700/40" />
                <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="auth-email-input" className="w-full pl-10 pr-4 py-3 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300" placeholder="you@email.com" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-navy-700 uppercase tracking-wider">Password</label>
              <div className="relative mt-2">
                <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-navy-700/40" />
                <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={isLogin ? undefined : 8} data-testid="auth-password-input" className="w-full pl-10 pr-4 py-3 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300" placeholder={isLogin ? "Your password" : "At least 8 characters"} />
              </div>
            </div>
            {err && <div data-testid="auth-error" className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{err}</div>}
            {isLogin && (
              <div className="text-right">
                <Link to="/forgot-password" data-testid="auth-forgot-password-link" className="text-sm text-navy-700/70 hover:text-coral-300">
                  Forgot password?
                </Link>
              </div>
            )}
            <button disabled={busy} type="submit" data-testid="auth-submit-btn" className="w-full bg-coral-300 text-white py-3 rounded-full font-semibold hover:bg-coral-400 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
              {busy ? "Please wait..." : isLogin ? "Sign in" : "Create account"}
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>
          <div className="text-center text-sm text-navy-700/70 mt-6">
            {isLogin ? "New to GalerieCop?" : "Already have an account?"}{" "}
            <button onClick={() => setIsLogin(!isLogin)} data-testid="auth-toggle-mode-btn" className="text-coral-300 font-semibold hover:text-coral-400">
              {isLogin ? "Create an account" : "Sign in"}
            </button>
          </div>
        </div>
        <div className="text-center mt-6 text-sm text-navy-700/60">
          <Link to="/" className="hover:text-coral-300">← Back to home</Link>
        </div>
      </div>
    </section>
  );
};

export default AuthPage;
